import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Smartphone, Save, ArrowLeft, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { logger } from '../utils/logger';
import { apiFetch } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext'; // Bu satırı ekle
import { validateAppData } from '../utils/validation';
import { sanitizeAppData } from '../utils/sanitizer';
import { sanitizeError } from '../utils/errorHandler';

const CreateApp = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isExpanded } = useSidebar(); // Bu satırı ekle
  const { register, handleSubmit, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (!user) {
        toast.error('Giriş yapmanız gerekiyor');
        navigate('/login');
        return;
      }

      // Input validation
      const validation = validateAppData(data);
      if (!validation.isValid) {
        Object.keys(validation.errors).forEach(field => {
          toast.error(validation.errors[field]);
        });
        return;
      }

      // Input sanitization
      const sanitizedData = sanitizeAppData(data);

      // API'ye yeni uygulama ekle
      const response = await apiFetch('/api/apps', {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
          title: sanitizedData.name,
          description: sanitizedData.description,
          platform: sanitizedData.platform,
          test_url: sanitizedData.testUrl,
          requirements: sanitizedData.requirements || null,
          status: 'active'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Uygulama oluşturulamadı');
      }

      const newApp = await response.json();

      
      toast.success('Uygulama başarıyla oluşturuldu');
      navigate('/my-apps');
      
    } catch (error) {
      logger.error('Uygulama oluşturulurken hata:', error);
      toast.error(sanitizeError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header Gradient */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 h-48 relative">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-400/20 rounded-full blur-3xl"></div>
        </div>
      </div>

      {/* Ana içerik - Sidebar animasyonu için margin ekle */}
      <div className={`relative -mt-32 pb-8 transition-all duration-300 ${
        isExpanded ? 'md:ml-72' : 'md:ml-18'
      }`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="backdrop-blur-xl bg-white/20 rounded-2xl p-6 shadow-xl border border-white/30">
              <div className="flex items-center mb-4">
                <Link
                  to="/my-apps"
                  className="inline-flex items-center text-sm text-white/80 hover:text-white transition-colors"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Uygulamalarıma Dön
                </Link>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white drop-shadow-md leading-tight">Yeni Uygulama Oluştur</h1>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="backdrop-blur-xl bg-white/60 rounded-2xl shadow-lg border border-white/40">
            <div className="px-8 py-6 border-b border-white/30">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md">
                  <Smartphone className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">Uygulama Bilgileri</h2>
              </div>
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Uygulama Adı
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Smartphone className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('name', {
                      required: 'Uygulama adı zorunludur',
                      minLength: {
                        value: 3,
                        message: 'Uygulama adı en az 3 karakter olmalıdır'
                      }
                    })}
                    type="text"
                    className="w-full pl-12 pr-4 py-3 backdrop-blur-sm bg-white/60 border border-white/40 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white/80 transition-all duration-300"
                    placeholder="Uygulama adınız"
                  />
                </div>
                {errors.name && (
                  <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
                  Açıklama
                </label>
                <textarea
                  {...register('description', {
                    required: 'Açıklama zorunludur',
                    minLength: {
                      value: 10,
                      message: 'Açıklama en az 10 karakter olmalıdır'
                    }
                  })}
                  rows={4}
                  className="w-full px-4 py-3 backdrop-blur-sm bg-white/60 border border-white/40 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white/80 transition-all duration-300"
                  placeholder="Uygulamanız hakkında detaylı bilgi verin"
                />
                {errors.description && (
                  <p className="mt-2 text-sm text-red-600">{errors.description.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="platform" className="block text-sm font-semibold text-gray-700 mb-2">
                  Platform
                </label>
                <select
                  {...register('platform', {
                    required: 'Platform seçimi zorunludur'
                  })}
                  className="w-full px-4 py-3 backdrop-blur-sm bg-white/60 border border-white/40 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white/80 transition-all duration-300"
                >
                  <option value="">Platform seçiniz</option>
                  <option value="Android">Android</option>
                  <option value="iOS">iOS</option>
                  <option value="Web">Web</option>
                  <option value="Desktop">Desktop</option>
                </select>
                {errors.platform && (
                  <p className="mt-2 text-sm text-red-600">{errors.platform.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="testUrl" className="block text-sm font-semibold text-gray-700 mb-2">
                  Test URL'si
                </label>
                <input
                  {...register('testUrl', {
                    required: 'Test URL\'si zorunludur',
                    pattern: {
                      value: /^https?:\/\/.+/,
                      message: 'Geçerli bir URL giriniz (http:// veya https:// ile başlamalı)'
                    }
                  })}
                  type="url"
                  className="w-full px-4 py-3 backdrop-blur-sm bg-white/60 border border-white/40 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white/80 transition-all duration-300"
                  placeholder="https://example.com/test"
                />
                {errors.testUrl && (
                  <p className="mt-2 text-sm text-red-600">{errors.testUrl.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="requirements" className="block text-sm font-semibold text-gray-700 mb-2">
                  Gereksinimler (Opsiyonel)
                </label>
                <textarea
                  {...register('requirements')}
                  rows={3}
                  className="w-full px-4 py-3 backdrop-blur-sm bg-white/60 border border-white/40 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white/80 transition-all duration-300"
                  placeholder="Test sırasında dikkat edilmesi gereken noktalar, özel gereksinimler vb."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-white/30">
                <Link
                  to="/my-apps"
                  className="inline-flex items-center px-6 py-3 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all duration-300"
                >
                  İptal
                </Link>
                <button
                  type="submit"
                  disabled={loading}
                  className="group inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:shadow-xl transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none transition-all duration-300"
                >
                  <Save className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform" />
                  {loading ? 'Oluşturuluyor...' : 'Uygulama Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateApp;